/**
 * PII scrub middleware
 *
 * Strips PII (email, phone, SSN, credit-card) from event properties before
 * they leave the client. Defensive — never trust caller to omit PII. The
 * cost of one leaked email under GDPR/CCPA dwarfs the cost of scrubbing
 * every property on every call.
 *
 * Patterns are intentionally broad: false positives (over-scrubbing) are
 * preferable to false negatives (leaking PII).
 */

const EMAIL_RE = /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}\b/g;
const PHONE_RE = /\b(?:\+?1[-.\s]?)?\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}\b/g;
const SSN_RE = /\b\d{3}-\d{2}-\d{4}\b/g;
// Credit card: 13-19 digits with optional separators. Conservative match.
const CC_RE = /\b(?:\d[ -]?){13,19}\b/g;

const REDACTED = '[REDACTED]';

/**
 * Allowlist of props that MUST NOT be scrubbed even if they match a PII
 * pattern. Used for known-safe identifiers like opaque UUIDs that may
 * coincidentally look like card numbers. Keep tight.
 */
const SCRUB_ALLOWLIST = new Set<string>([
  'session_id',
  'tenant_id',
  'userId',
  'user_id',
  'timestamp',
  'request_id',
]);

/**
 * Scrub a single string value for PII patterns. Replaces matches with
 * [REDACTED] in-place.
 */
export function scrubString(value: string): string {
  return value
    .replace(EMAIL_RE, REDACTED)
    .replace(SSN_RE, REDACTED)
    .replace(CC_RE, REDACTED)
    .replace(PHONE_RE, REDACTED);
}

/**
 * Scrub a property bag. Returns a new object — never mutates input.
 * Keys in SCRUB_ALLOWLIST pass through untouched. All other string
 * values are scanned for PII patterns. Non-string values pass through
 * (numbers, booleans, null, undefined cannot carry PII patterns).
 */
export function scrubProps<T extends Record<string, unknown>>(props: T): T {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(props)) {
    if (SCRUB_ALLOWLIST.has(key)) {
      out[key] = value;
      continue;
    }
    if (typeof value === 'string') {
      out[key] = scrubString(value);
    } else {
      out[key] = value;
    }
  }
  return out as T;
}
